import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectsApi, clientsApi } from '../api';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

export function ProjectFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get('clientId');

  const [formData, setFormData] = useState({
    name: '',
    clientId: preselectedClientId || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Carica lista clienti
  const { data: clientsData, isLoading: isLoadingClients } = useQuery({
    queryKey: ['clients', { active: true }],
    queryFn: () => clientsApi.list({ active: true, limit: 100 }),
  });

  const clients = clientsData?.data.data.clients || [];

  // Mutation per creazione
  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => projectsApi.create({
      name: data.name,
      clientId: data.clientId,
      config: createInitialConfig(),
    }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate(`/projects/${response.data.data.project.id}`);
    },
    onError: (error: Error) => {
      setErrors({ submit: error.message });
    },
  });

  // Configurazione iniziale vuota
  function createInitialConfig() {
    return {
      site: {
        shell: {
          brand: {
            name: '',
            logo: null,
          },
          nav: {
            items: [],
          },
          footer: {
            columns: [],
            legal: [],
          },
        },
      },
      routing: {
        canonical: { stripTrailingSlash: true },
        redirects: [],
      },
      dataSources: [],
      pages: [],
      bindingSpecs: [],
      featureFlags: {},
    };
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Il nome è obbligatorio';
    }

    if (!formData.clientId) {
      newErrors.clientId = 'Seleziona un cliente';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    createMutation.mutate(formData);
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Indietro
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Nuovo progetto</h1>
        <p className="text-gray-600 mt-1">
          Crea una nuova configurazione per un sito
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
            {errors.submit}
          </div>
        )}

        <div className="card p-6 space-y-4">
          <div>
            <label htmlFor="clientId" className="label">
              Cliente <span className="text-red-500">*</span>
            </label>
            {isLoadingClients ? (
              <div className="input bg-gray-100 flex items-center">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Caricamento clienti...
              </div>
            ) : clients.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-md text-sm">
                Nessun cliente disponibile.{' '}
                <button
                  type="button"
                  onClick={() => navigate('/clients/new')}
                  className="underline font-medium"
                >
                  Crea un cliente
                </button>{' '}
                prima di creare un progetto.
              </div>
            ) : (
              <select
                id="clientId"
                value={formData.clientId}
                onChange={(e) => handleChange('clientId', e.target.value)}
                className={`input ${errors.clientId ? 'border-red-500' : ''}`}
              >
                <option value="">Seleziona un cliente...</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name} ({client.code})
                  </option>
                ))}
              </select>
            )}
            {errors.clientId && (
              <p className="text-red-500 text-xs mt-1">{errors.clientId}</p>
            )}
          </div>

          <div>
            <label htmlFor="name" className="label">
              Nome progetto <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Es. Sito principale, Redesign 2025, ..."
              className={`input ${errors.name ? 'border-red-500' : ''}`}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Un nome descrittivo per identificare questa configurazione
            </p>
          </div>
        </div>

        <div className="card p-6 bg-gray-50">
          <h3 className="font-medium text-gray-900 mb-2">Cosa succede dopo?</h3>
          <p className="text-sm text-gray-600">
            Verrà creato un progetto con una configurazione iniziale vuota.
            Potrai poi modificare shell, pagine, sezioni e binding dall'editor del progetto.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || clients.length === 0}
            className="btn-primary"
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creazione...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Crea progetto
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
